import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    DialogContentText,
    Button,
    Snackbar,
    TextField,
    CircularProgress,
    Grid,
    Paper,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Save as SaveIcon,
    Security as AdminIcon,
    VerifiedUser as AdvancedUserIcon,
    Person as UserIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { accountApiEntryPoint, argosApiEntryPoint } from '../GlobalConfig';
import { auth } from '../firebase/firebase';

type Role = 'Admin' | 'AdvancedUser' | 'User';

interface UserInfo {
    displayName: string;
    email: string | null;
    creationTime: string;
    lastSignInTime: string;
}

interface UserRoleEntry {
    accountId: string;
    institutionId: string;
    role: Role;
    userInfo: UserInfo;
}

interface InstitutionUserManagerProps {
    shortInstitutionId: string;
}

interface ConfirmationDialogState {
    open: boolean;
    action?: 'delete' | 'update';
    accountId?: string;
    newRole?: Role;
}

interface SnackbarState {
    open: boolean;
    message: string;
    error: boolean;
}

const ROLE_ICONS: Record<Role, JSX.Element> = {
    Admin: <AdminIcon color="primary" />,
    AdvancedUser: <AdvancedUserIcon color="primary" />,
    User: <UserIcon color="primary" />,
};

const ROLES: Role[] = ['Admin', 'AdvancedUser', 'User'];

const InstitutionUserManager: React.FC<InstitutionUserManagerProps> = ({
    shortInstitutionId
}) => {
    const [users, setUsers] = useState<UserRoleEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [editRoles, setEditRoles] = useState<Record<string, Role>>({});
    const [page, setPage] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [confirmation, setConfirmation] = useState<ConfirmationDialogState>({ open: false });
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        error: false,
    });

    const [newEmail, setNewEmail] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [newRole, setNewRole] = useState<Role>('User');
    const [newPassword, setNewPassword] = useState('');
    const [addingUser, setAddingUser] = useState(false);

    const showError = (message: string) =>
        setSnackbar({ open: true, message, error: true });
    const showSuccess = (message: string) =>
        setSnackbar({ open: true, message, error: false });

    const addUser = async () => {
        if (!newEmail.trim() || !newDisplayName.trim() || !newPassword.trim()) {
            alert('Email, Display Name and Password are required');
            return;
        }

        setAddingUser(true);
        try {
            let authToken = await auth.currentUser?.getIdToken(true)
            // Step 1: Create the user
            const createRes = await fetch(accountApiEntryPoint + `/admin/create-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + authToken },
                body: JSON.stringify({
                    email: newEmail.trim(),
                    password: newPassword.trim(),
                    displayName: newDisplayName.trim(),
                }),
            });

            if (createRes.status !== 200) {
                alert('Failed to create user');
                return;
            }

            const newUserData: any = await createRes.json()

            // Step 2: Assign role
            const assignRes = await fetch(accountApiEntryPoint + `/admin/institution/role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + authToken },
                body: JSON.stringify({
                    accountId: newUserData.uid,
                    role: newRole,
                    institutionId: `https://openalex.org/${shortInstitutionId}`,
                }),
            });

            if (assignRes.status === 201) {
                alert('User added successfully');
                setNewEmail('');
                setNewDisplayName('');
                setNewPassword('');
                setNewRole('User');
                await fetchUsers();
            } else {
                alert('User created, but failed to assign role');
            }
        } catch (err) {
            alert('An error occurred while adding user');
        } finally {
            setAddingUser(false);
        }
    };


    const fetchUsers = async () => {
        try {
            setLoading(true);
            let authToken = await auth.currentUser?.getIdToken(true)

            const response = await fetch(
                accountApiEntryPoint + `/admin/institution/${shortInstitutionId}/role/search`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + authToken
                    }
                }
            );
            const data: UserRoleEntry[] = await response.json();
            setUsers(data);
        } catch {
            showError('Failed to load users.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (accountId: string) => {
        try {
            let authToken = await auth.currentUser?.getIdToken(true)

            const res = await fetch(
                accountApiEntryPoint + `/admin/institution/role`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authToken
                    },
                    body: JSON.stringify({
                        accountId,
                        institutionId: `https://openalex.org/${shortInstitutionId}`,
                    }),
                }
            );
            if (res.status === 204) {
                showSuccess('User deleted.');
                await fetchUsers();
            } else {
                showError('Delete not successful.');
            }
        } catch {
            showError('Delete failed.');
        }
    };

    const handleRoleUpdate = async (accountId: string, newRole: Role) => {
        try {
            let authToken = await auth.currentUser?.getIdToken(true)

            const res = await fetch(
                accountApiEntryPoint + `/admin/institution/role`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authToken
                    },
                    body: JSON.stringify({
                        accountId,
                        institutionId: `https://openalex.org/${shortInstitutionId}`,
                        role: newRole,
                    }),
                }
            );
            if (res.status === 200) {
                showSuccess('Role updated.');
                await fetchUsers();
            } else {
                showError('Role update not successful.');
            }
        } catch {
            showError('Failed to update role.');
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter((user) => {
            const lowerSearch = search.toLowerCase();
            return (
                user.accountId.toLowerCase().includes(lowerSearch) ||
                user.role.toLowerCase().includes(lowerSearch) ||
                user.userInfo?.displayName.toLowerCase().includes(lowerSearch) ||
                user.userInfo?.email?.toLowerCase().includes(lowerSearch)
            );
        });
    }, [users, search]);

    const columns: GridColDef[] = [
        {
            field: 'accountId',
            headerName: 'User ID',
            flex: 1,
        },
        {
            field: 'displayName',
            headerName: 'Display Name',
            flex: 1.5,
            renderCell: (params) => params.row.userInfo?.displayName ?? ''
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 2,
            renderCell: (params) => params.row.userInfo?.email ?? ''
        },
        {
            field: 'creationTime',
            headerName: 'Creation Time',
            flex: 1.5,
            renderCell: (params) => params.row.userInfo?.creationTime ?? ''
        },
        {
            field: 'lastSignInTime',
            headerName: 'Last Sign-in',
            flex: 1.5,
            renderCell: (params) => params.row.userInfo?.lastSignInTime ?? ''
        },
        {
            field: 'role',
            headerName: 'Role',
            flex: 1.5,
            renderCell: (params) => {
                const role = params.row.role as Role;
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        {ROLE_ICONS[role]}
                        <Typography>{role}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'editRole',
            headerName: 'Change Role',
            flex: 2,
            renderCell: (params) => {
                const accountId = params.row.accountId;
                const currentRole = params.row.role as Role;
                const selectedRole = editRoles[accountId] || currentRole;

                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Select
                            value={selectedRole}
                            onChange={(e) =>
                                setEditRoles((prev) => ({
                                    ...prev,
                                    [accountId]: e.target.value as Role,
                                }))
                            }
                            size="small"
                        >
                            {ROLES.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </Select>
                        {selectedRole !== currentRole && (
                            <IconButton
                                onClick={() =>
                                    setConfirmation({
                                        open: true,
                                        action: 'update',
                                        accountId,
                                        newRole: selectedRole,
                                    })
                                }
                            >
                                <SaveIcon color="primary" />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            flex: 0.5,
            renderCell: (params) => (
                <IconButton
                    color="primary"
                    onClick={() =>
                        setConfirmation({
                            open: true,
                            action: 'delete',
                            accountId: params.row.accountId,
                        })
                    }
                >
                    <DeleteIcon />
                </IconButton>
            ),
        },
    ];

    const handleConfirm = async () => {
        const { action, accountId, newRole } = confirmation;
        setConfirmation({ open: false });

        if (action === 'delete' && accountId) {
            await handleDelete(accountId);
        } else if (action === 'update' && accountId && newRole) {
            await handleRoleUpdate(accountId, newRole);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <>
            <Typography gutterBottom variant="h3" component="div" sx={{ color: '#003366', fontWeight: 'bold' }}>
                Institutional users
            </Typography>

            <Box
                sx={{
                    mb: 3,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                <TextField
                    label="Email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                />
                <TextField
                    label="Display Name"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                />
                <TextField
                    label="Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="add-user-role-label">Role</InputLabel>
                    <Select
                        labelId="add-user-role-label"
                        value={newRole}
                        label="Role"
                        onChange={(e) => setNewRole(e.target.value as Role)}
                    >
                        {ROLES.map((r) => (
                            <MenuItem key={r} value={r}>
                                {r}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={addingUser}
                    onClick={addUser}
                >
                    Add User
                </Button>
            </Box>

            <Box sx={{ width: '80vw', height: '80vh' }}>

                <TextField
                    label="Search Users"
                    variant="outlined"
                    size="small"
                    fullWidth
                    margin="normal"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid<UserRoleEntry>
                        rows={filteredUsers}
                        columns={columns}
                        getRowId={(row) => row.accountId}
                        pageSizeOptions={[10]}
                        paginationModel={{ pageSize: 10, page }}
                        onPaginationModelChange={(model) => setPage(model.page)}
                        loading={loading}
                        autoHeight
                    />
                )}

                <Dialog open={confirmation.open} onClose={() => setConfirmation({ open: false })}>
                    <DialogTitle>Confirm Action</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to{' '}
                            {confirmation.action === 'delete'
                                ? 'delete this user?'
                                : `change role to "${confirmation.newRole}"?`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmation({ open: false })}>Cancel</Button>
                        <Button onClick={handleConfirm} color="primary" autoFocus>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    message={snackbar.message}
                    autoHideDuration={4000}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    ContentProps={{
                        sx: {
                            backgroundColor: snackbar.error ? 'error.main' : 'success.main',
                            color: '#fff',
                        },
                    }}
                />
            </Box>
        </>
    );
};

export default InstitutionUserManager;